import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';

const AdminReports = () => {
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState('open');
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [moderatorNote, setModeratorNote] = useState('');

  useEffect(() => {
    loadReports();
    loadStats();
  }, [filter]);

  const loadReports = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/admin/reports?status=${filter}`);
      setReports(response.data.reports);
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axiosInstance.get('/admin/reports/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleStatusUpdate = async (reportId, newStatus) => {
    try {
      await axiosInstance.put(`/admin/reports/${reportId}`, {
        status: newStatus,
        moderatorNote
      });
      
      loadReports();
      loadStats();
      setSelectedReport(null);
      setModeratorNote('');
    } catch (error) {
      console.error('Error updating report:', error);
      alert('Fehler beim Aktualisieren der Meldung');
    }
  };

  const getReasonLabel = (reason) => {
    const reasons = {
      spam: 'Spam',
      offensive: 'Beleidigend/Hassrede',
      inappropriate: 'Unpassender Inhalt',
      harassment: 'Belästigung',
      misinformation: 'Fehlinformationen',
      copyright: 'Urheberrechtsverletzung',
      other: 'Anderes'
    };
    return reasons[reason] || reason;
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: 'badge-error',
      reviewing: 'badge-warning',
      resolved: 'badge-success',
      dismissed: 'badge-neutral'
    };
    return badges[status] || 'badge-neutral';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('de-DE');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Meldungen verwalten</h1>
          <Link to="/admin" className="btn btn-ghost">
            ← Zurück zum Admin-Panel
          </Link>
        </div>

        {/* Statistiken */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="stat bg-base-200 rounded-lg">
            <div className="stat-title">Gesamt</div>
            <div className="stat-value">{stats.totalReports || 0}</div>
          </div>
          <div className="stat bg-base-200 rounded-lg">
            <div className="stat-title">Heute</div>
            <div className="stat-value">{stats.todayReports || 0}</div>
          </div>
          <div className="stat bg-base-200 rounded-lg">
            <div className="stat-title">Offen</div>
            <div className="stat-value text-error">{stats.byStatus?.open || 0}</div>
          </div>
          <div className="stat bg-base-200 rounded-lg">
            <div className="stat-title">Bearbeitet</div>
            <div className="stat-value text-success">{stats.byStatus?.resolved || 0}</div>
          </div>
        </div>

        {/* Filter */}
        <div className="tabs tabs-boxed mb-6">
          {['all', 'open', 'reviewing', 'resolved', 'dismissed'].map(status => (
            <button
              key={status}
              className={`tab ${filter === status ? 'tab-active' : ''}`}
              onClick={() => setFilter(status)}
            >
              {status === 'all' ? 'Alle' : 
               status === 'open' ? 'Offen' :
               status === 'reviewing' ? 'In Bearbeitung' :
               status === 'resolved' ? 'Erledigt' :
               'Abgelehnt'}
            </button>
          ))}
        </div>

        {/* Reports List */}
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th>Datum</th>
                <th>Grund</th>
                <th>Gemeldet von</th>
                <th>Post-Inhalt</th>
                <th>Status</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {reports.map(report => (
                <tr key={report._id}>
                  <td className="text-xs">
                    {formatDate(report.createdAt)}
                  </td>
                  <td>
                    <span className="badge badge-outline">
                      {getReasonLabel(report.reason)}
                    </span>
                  </td>
                  <td>{report.reporterId?.username}</td>
                  <td>
                    <div className="truncate max-w-xs">
                      {report.postId?.content || 'Post gelöscht'}
                    </div>
                    {report.message && (
                      <div className="text-xs text-base-content/70 mt-1">
                        Kommentar: {report.message}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${getStatusBadge(report.status)}`}>
                      {report.status}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button
                        className="btn btn-xs btn-outline"
                        onClick={() => setSelectedReport(report)}
                      >
                        Details
                      </button>
                      {report.postId && (
                        <Link
                          to={`/thread/${report.postId.thread?._id}#${report.postId._id}`}
                          className="btn btn-xs btn-primary"
                          target="_blank"
                        >
                          Zum Post
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {reports.length === 0 && (
            <div className="text-center py-8 text-base-content/60">
              Keine Meldungen gefunden
            </div>
          )}
        </div>

        {/* Detail Modal */}
        {selectedReport && (
          <div className="modal modal-open">
            <div className="modal-box max-w-2xl">
              <h3 className="font-bold text-lg mb-4">Meldung Details</h3>
              
              <div className="space-y-4">
                <div>
                  <strong>Grund:</strong> {getReasonLabel(selectedReport.reason)}
                </div>
                <div>
                  <strong>Gemeldet von:</strong> {selectedReport.reporterId?.username}
                </div>
                <div>
                  <strong>Datum:</strong> {formatDate(selectedReport.createdAt)}
                </div>
                {selectedReport.message && (
                  <div>
                    <strong>Nachricht:</strong>
                    <p className="mt-1 p-2 bg-base-200 rounded">{selectedReport.message}</p>
                  </div>
                )}
                <div>
                  <strong>Post-Inhalt:</strong>
                  <div 
                    className="mt-1 p-2 bg-base-200 rounded"
                    dangerouslySetInnerHTML={{ __html: selectedReport.postId?.contentHtml || 'Post gelöscht' }}
                  />
                </div>
                
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Moderator-Notiz</span>
                  </label>
                  <textarea
                    className="textarea textarea-bordered"
                    value={moderatorNote}
                    onChange={(e) => setModeratorNote(e.target.value)}
                    placeholder="Interne Notiz zur Bearbeitung..."
                    rows={3}
                  />
                </div>
              </div>

              <div className="modal-action">
                <button
                  className="btn btn-ghost"
                  onClick={() => {
                    setSelectedReport(null);
                    setModeratorNote('');
                  }}
                >
                  Abbrechen
                </button>
                {selectedReport.status === 'open' && (
                  <>
                    <button
                      className="btn btn-warning"
                      onClick={() => handleStatusUpdate(selectedReport._id, 'reviewing')}
                    >
                      In Bearbeitung
                    </button>
                    <button
                      className="btn btn-error"
                      onClick={() => handleStatusUpdate(selectedReport._id, 'dismissed')}
                    >
                      Ablehnen
                    </button>
                    <button
                      className="btn btn-success"
                      onClick={() => handleStatusUpdate(selectedReport._id, 'resolved')}
                    >
                      Erledigen
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="modal-backdrop" onClick={() => setSelectedReport(null)}></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReports;
